<script setup lang="ts">
import {
    ref,
    useTemplateRef,
    watch,
    type ComputedRef,
    type HTMLAttributes,
} from 'vue';
import { useMagicKeys } from '@vueuse/core';
import { ChevronRight, Clock2, Search, X } from 'lucide-vue-next';
import type { FocusOutsideEvent, PointerDownOutsideEvent } from 'reka-ui';
import CardPreview from '@/components/CardPreview.vue';
import { Button } from '@/components/ui/button';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from '@/components/ui/input-group';
import { Kbd } from '@/components/ui/kbd';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@/components/ui/popover';
import { SelectLabel } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import useDevice from '@/composables/useDevice.ts';
import useSearch from '@/composables/useSearch.ts';
import { cn } from '@/lib/utils.ts';

const props = defineProps<{
    class?: HTMLAttributes['class'];
}>();

const searchInput = useTemplateRef('searchInput');
const { query, results, count, reset, isPending, track, recommendations } =
    useSearch();
const device = useDevice();

const isOpen = ref(false);
const isFocused = ref(false);

if (device.isDesktop) {
    const keys = useMagicKeys();
    const specialK = keys[`${device.specialKey}+K`] as ComputedRef<boolean>;

    watch(specialK, (pressed) => {
        if (pressed) {
            searchInput.value?.$el?.focus();
        }
    });
}

function focus() {
    searchInput.value?.$el?.focus();
}

function clear() {
    reset();
    isOpen.value = false;
    searchInput.value?.$el?.focus();
}

function onFocus() {
    isFocused.value = true;
    isOpen.value = true;
}

function onBlur() {
    isFocused.value = false;
}

function onInteractOutside(e: PointerDownOutsideEvent | FocusOutsideEvent) {
    e.preventDefault();

    setTimeout(() => {
        if (query.value !== '' || isFocused.value) {
            return;
        }

        isOpen.value = false;
    }, 10);
}

defineExpose({
    focus,
});
</script>

<template>
    <Popover v-model:open="isOpen">
        <PopoverAnchor as-child>
            <div
                :class="
                    cn(
                        'relative w-full max-w-96 transition-[max-width] duration-300',
                        {
                            'max-w-xl': isFocused || query !== '',
                        },
                        props.class
                    )
                "
            >
                <InputGroup
                    class="has-[[data-slot=input-group-control]:focus-visible]:ring-inset"
                    :class="{ 'rounded-b-none': isOpen }"
                >
                    <InputGroupInput
                        ref="searchInput"
                        placeholder="Dokumente und Seiten durchsuchen"
                        size="lg"
                        v-model="query"
                        @focus="onFocus"
                        @blur="onBlur"
                    />
                    <InputGroupAddon>
                        <Search />
                    </InputGroupAddon>
                    <InputGroupAddon
                        v-if="device.isDesktop && !isFocused && query === ''"
                        align="inline-end"
                    >
                        <Kbd>{{ device.specialKeySymbol }} K</Kbd>
                    </InputGroupAddon>
                    <InputGroupAddon
                        v-if="query !== ''"
                        align="inline-end"
                    >
                        <InputGroupButton
                            aria-label="Suche zurücksetzen"
                            size="icon-xs"
                            @click="clear"
                        >
                            <X />
                        </InputGroupButton>
                    </InputGroupAddon>
                </InputGroup>
            </div>
        </PopoverAnchor>
        <PopoverContent
            align="start"
            :side-offset="0"
            class="max-h-[50vh] w-(--reka-popover-trigger-width) divide-y overflow-y-auto rounded-t-none border-t-0 px-0 py-1"
            @open-auto-focus.prevent
            @interact-outside="onInteractOutside"
            @focus="onFocus"
        >
            <template v-if="query !== ''">
                <template v-if="!isPending && count > 0">
                    <div
                        v-if="results.files.length > 0"
                        class="pb-3"
                    >
                        <SelectLabel class="px-3">Dokumente</SelectLabel>
                        <div class="grid auto-rows-auto grid-cols-1 gap-2 px-3">
                            <CardPreview
                                v-for="file in results.files"
                                :key="file.uid"
                                type="file"
                                :uid="file.uid"
                                :title="file.title"
                                :url="file.url"
                                :preview="file.preview"
                                :badge="file.badge"
                                :is-new="file.isNew"
                                @select="track"
                            >
                                <p
                                    v-html="file.content"
                                    class="p-mini text-muted-foreground [&_em]:highlight [&_em]:not-italic"
                                ></p>
                            </CardPreview>
                        </div>
                    </div>
                    <div
                        v-if="results.entries.length > 0"
                        class="pb-3"
                    >
                        <SelectLabel class="px-3"
                            >Weitere Suchergebnisse</SelectLabel
                        >
                        <div class="space-y-2 px-3">
                            <CardPreview
                                v-for="entry in results.entries"
                                :key="entry.uid"
                                type="entry"
                                :uid="entry.uid"
                                :title="entry.title"
                                :url="entry.url"
                                @select="track"
                            >
                                <template #title>
                                    <span
                                        class="text-muted-foreground font-normal"
                                        v-for="ancestor in entry.ancestors"
                                    >
                                        {{ ancestor }}
                                        <ChevronRight
                                            class="inline-block size-3.5"
                                        />
                                    </span>
                                    <span
                                        class="[&_em]:highlight [&_em]:not-italic"
                                        v-html="entry.title"
                                    ></span>
                                </template>
                                <template #default>
                                    <p
                                        v-if="entry.content !== ''"
                                        v-html="entry.content"
                                        class="p-mini text-muted-foreground [&_em]:highlight [&_em]:not-italic"
                                    ></p>
                                </template>
                            </CardPreview>
                        </div>
                    </div>
                </template>
                <template v-else-if="!isPending && count === 0">
                    <p class="text-muted-foreground p-3 text-sm">
                        Keine Ergebnisse für den Suchbegriff „{{ query }}“.
                    </p>
                </template>
                <div
                    v-else
                    class="space-y-2 px-3 py-2"
                >
                    <Skeleton class="h-3 w-24" />
                    <Skeleton class="h-24 w-full" />
                    <Skeleton class="h-24 w-full" />
                </div>
            </template>
            <div
                v-else
                class="divide-y"
            >
                <div
                    v-if="recommendations.recentSearches.length > 0"
                    class="px-1 pb-1"
                >
                    <SelectLabel class="px-2">Letzte Suchbegriffe</SelectLabel>
                    <Button
                        v-for="search in recommendations.recentSearches"
                        variant="ghost"
                        size="xs"
                        class="w-full justify-start"
                        @click="query = search"
                    >
                        <Clock2 />
                        {{ search }}
                    </Button>
                </div>
                <div
                    v-for="preset in recommendations.presets"
                    class="px-3"
                >
                    <SelectLabel class="px-0">{{ preset.label }}</SelectLabel>
                    <div class="flex flex-wrap gap-1.5 pb-3">
                        <Button
                            v-for="item in preset.items"
                            variant="secondary"
                            size="xs"
                            class="text-[0.85rem]"
                            @click="query = item"
                        >
                            {{ item }}
                        </Button>
                    </div>
                </div>
            </div>
        </PopoverContent>
    </Popover>
</template>
